/**
 * Words under the notes.
 *
 * Lyrics are typed a line at a time rather than a syllable at a time, because that is how
 * they arrive: someone has the verse already and wants it under the tune. {@link typeLine}
 * takes the line as it would be written on paper -- words separated by spaces, syllables of
 * one word by hyphens -- and lays it across a selection one syllable per note, which is the
 * gesture Sibelius and MuseScore both offer and the reason neither asks the user to say
 * "begin", "middle" or "end" for anything.
 *
 * The individual setters are still here, because an edit to one syllable should not have to
 * retype the line.
 */

import type { EventId } from "../document/ids";
import type { Lyric, MusicEvent, NoteEvent } from "../document/types";
import type { Score } from "../document/types";
import type { Command } from "../commands/types";
import { locateEvent } from "../document/query";
import type { EventLocation } from "../document/query";
import { selectedLocations } from "./selection";
import type { Selection } from "./selection";

interface NoteAt extends EventLocation {
  readonly event: NoteEvent;
}

function isNote(location: EventLocation): location is NoteAt {
  return location.event.kind === "note";
}

/** The selected notes in reading order. Rests take no words. */
function notes(score: Score, selection: Selection): readonly NoteAt[] {
  return selectedLocations(score, selection).filter(isNote);
}

/** The lyrics of a note with one verse replaced, or removed when the text is empty. */
function withVerse(event: NoteEvent, lyric: Lyric): readonly Lyric[] {
  const others = event.lyrics.filter((held) => held.verse !== lyric.verse);
  const kept = lyric.text === "" ? others : [...others, lyric];
  return [...kept].sort((one, other) => one.verse - other.verse);
}

function setOn(event: NoteEvent, lyric: Lyric): Command {
  return { kind: "setEventProps", eventId: event.id, props: { lyrics: withVerse(event, lyric) } };
}

function noteAt(score: Score, eventId: EventId): NoteEvent | string {
  const found: { readonly event: MusicEvent } | null = locateEvent(score, eventId);
  if (found === null) return "there is no such note";
  if (found.event.kind !== "note") return "a rest is not sung";
  return found.event;
}

/** Write one syllable under one note. An empty text takes that verse away from it. */
export function setSyllable(
  score: Score,
  eventId: EventId,
  verse: number,
  text: string,
  syllabic: Lyric["syllabic"] = "single",
  extend = false,
): Command | string {
  if (!Number.isInteger(verse) || verse < 1) return "a verse is numbered from one";
  const event = noteAt(score, eventId);
  if (typeof event === "string") return event;
  return setOn(event, { verse, text: text.trim(), syllabic, extend });
}

/** Draw or remove the line that carries a held syllable across the notes after it. */
export function setExtend(
  score: Score,
  eventId: EventId,
  verse: number,
  extend: boolean,
): Command | string {
  const event = noteAt(score, eventId);
  if (typeof event === "string") return event;
  const held = event.lyrics.find((lyric) => lyric.verse === verse);
  if (held === undefined) return "that note has no syllable in that verse";
  return setOn(event, { ...held, extend });
}

/**
 * One line of a verse, as syllables.
 *
 * Words are separated by whitespace and the syllables of a word by hyphens, so `glo-ri-a in
 * ex-cel-sis` is six syllables in three words. Each syllable remembers where in its word it
 * fell, which is what MusicXML calls `syllabic` and what draws the hyphens between them.
 */
export function syllablesOf(line: string): readonly Omit<Lyric, "verse" | "extend">[] {
  const out: { text: string; syllabic: Lyric["syllabic"] }[] = [];
  for (const word of line.split(/\s+/u).filter((piece) => piece !== "")) {
    const parts = word.split("-").filter((piece) => piece !== "");
    parts.forEach((text, index) => {
      const first = index === 0;
      const last = index === parts.length - 1;
      const syllabic = first && last ? "single" : first ? "begin" : last ? "end" : "middle";
      out.push({ text, syllabic });
    });
  }
  return out;
}

/**
 * Lay a line of words across the selected notes, one syllable per note.
 *
 * Running out of notes is a refusal rather than a truncation: silently dropping the end of
 * a line loses words the user typed, and there is no way to see that it happened. Running
 * out of *words* first is fine -- a verse is often shorter than the passage it starts.
 */
export function typeLine(
  score: Score,
  selection: Selection,
  line: string,
  verse = 1,
): Command | string {
  if (!Number.isInteger(verse) || verse < 1) return "a verse is numbered from one";
  const sung = notes(score, selection);
  if (sung.length === 0) return "select the notes the words go under";

  const syllables = syllablesOf(line);
  if (syllables.length === 0) return "there are no words to write";
  if (syllables.length > sung.length) {
    return `there are ${syllables.length} syllables and only ${sung.length} notes to sing them on`;
  }

  const commands = syllables.map((syllable, index) =>
    setOn(sung[index].event, { verse, ...syllable, extend: false }),
  );
  return { kind: "batch", label: "Write lyrics", commands };
}

/** Take the words off the selected notes: one verse, or every verse when `verse` is null. */
export function clearLyrics(
  score: Score,
  selection: Selection,
  verse: number | null = null,
): Command | string {
  const sung = notes(score, selection).filter((at) => at.event.lyrics.length > 0);
  if (sung.length === 0) return "none of the selected notes carry words";

  const commands = sung.map((at) => ({
    kind: "setEventProps" as const,
    eventId: at.event.id,
    props: {
      lyrics: verse === null ? [] : at.event.lyrics.filter((lyric) => lyric.verse !== verse),
    },
  }));
  return { kind: "batch", label: "Clear lyrics", commands };
}

/** How many verses the score has words for, which is what a verse picker offers. */
export function verseCount(score: Score): number {
  let most = 0;
  for (const part of score.parts) {
    for (const measure of Object.values(part.measures)) {
      for (const voice of measure.voices) {
        for (const event of voice.events) {
          if (event.kind !== "note") continue;
          for (const lyric of event.lyrics) most = Math.max(most, lyric.verse);
        }
      }
    }
  }
  return most;
}
